// =============================================================================
// SupportContact.jsx - Support & Contact Settings Component
// =============================================================================
// Tier-based support contact forms
// - Different forms for each subscription tier (free/trial/basic/standard/premium)
// - Netlify Forms submission
// - Auto-filled subject based on tier
// - Email contact for all tiers
// - Phone option for Premium tier only
// =============================================================================

import React, { useState } from 'react';

const SupportContact = ({
  clubInfo,
  subscriptionTier = 'free',
  subscriptionStatus = 'trial',
  user,
  showSuccess,
  isAdmin
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    email: user?.email || '',
    phone: '',
    detail: '',
    contactMethod: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ===========================================================================
  // TIER CONFIGURATION
  // ===========================================================================
  const getTierConfig = () => {
    // Determine effective tier (trial users get treated as their trial tier)
    const effectiveTier = subscriptionStatus === 'trial' ? subscriptionTier : subscriptionTier;
    
    const configs = {
      free: {
        subject: 'Free user requires support',
        title: 'Free Support',
        description: 'Submit your support request and we\'ll get back to you via email.',
        showPhone: false,
        badge: null // No badge for free
      },
      trial: {
        subject: 'Trial user requires support',
        title: 'Trial Support',
        description: 'Submit your support request and we\'ll help you get the most out of your trial.',
        showPhone: false,
        badge: null // No badge for trial
      },
      basic: {
        subject: 'Basic user requires email support',
        title: 'Email Support',
        description: 'Submit your support request and our team will respond via email.',
        showPhone: false,
        badge: {
          text: 'BASIC SUPPORT',
          backgroundColor: '#6b7280',
          color: 'white'
        }
      },
      standard: {
        subject: 'Standard user requires Priority email support',
        title: 'Priority Email Support',
        description: 'Submit your support request and our team will prioritize your inquiry.',
        showPhone: false,
        badge: {
          text: 'STANDARD SUPPORT',
          backgroundColor: '#6b7280',
          color: 'white'
        }
      },
      premium: {
        subject: 'Premium user requires Priority support',
        title: 'Premium Support',
        description: 'Choose your preferred contact method. Premium users receive priority support via phone or email.',
        showPhone: true,
        badge: {
          text: 'PREMIUM',
          backgroundColor: '#f59e0b',
          color: 'white'
        }
      }
    };

    // Handle trial status
    if (subscriptionStatus === 'trial') {
      return configs.trial;
    }

    return configs[effectiveTier] || configs.free;
  };

  const tierConfig = getTierConfig();

  // ===========================================================================
  // FORM VALIDATION
  // ===========================================================================
  const isFormValid = () => {
    const hasDescription = formData.description.trim().length > 0;
    const hasEmail = formData.email && formData.email.includes('@');
    const hasDetail = formData.detail.trim().length > 0;
    
    // Premium users choosing phone must provide phone number
    if (tierConfig.showPhone && formData.contactMethod === 'phone') {
      const hasPhone = formData.phone.trim().length >= 10;
      return hasDescription && hasEmail && hasDetail && hasPhone;
    }
    
    return hasDescription && hasEmail && hasDetail;
  };

  // ===========================================================================
  // HANDLERS
  // ===========================================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare form data for Netlify
      const netlifyFormData = new FormData();
      netlifyFormData.append('form-name', 'support-contact');
      netlifyFormData.append('subject', tierConfig.subject);
      netlifyFormData.append('subscription-tier', subscriptionTier);
      netlifyFormData.append('subscription-status', subscriptionStatus);
      netlifyFormData.append('club-name', clubInfo?.name || 'Unknown');
      netlifyFormData.append('club-id', clubInfo?.clubId || 'Unknown');
      netlifyFormData.append('description', formData.description);
      netlifyFormData.append('email', formData.email);
      netlifyFormData.append('detail', formData.detail);
      
      if (tierConfig.showPhone) {
        netlifyFormData.append('contact-method', formData.contactMethod);
        netlifyFormData.append('phone', formData.phone);
      }

      // Submit to Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        body: netlifyFormData
      });

      if (!response.ok) {
        throw new Error('Failed to submit support request');
      }

      // Success!
      showSuccess('Support request submitted successfully! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        description: '',
        email: user?.email || '',
        phone: '',
        detail: '',
        contactMethod: 'email'
      });
      
      // Close dropdown after success
      setTimeout(() => {
        setIsExpanded(false);
      }, 2000);

    } catch (err) {
      console.error('Error submitting support request:', err);
      setError('Failed to submit request. Please try again or email us directly at pitchalloaustralia@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (
    <>
      {/* Hidden Netlify form for form detection */}
      <form name="support-contact" netlify="true" hidden>
        <input type="text" name="subject" />
        <input type="text" name="subscription-tier" />
        <input type="text" name="subscription-status" />
        <input type="text" name="club-name" />
        <input type="text" name="club-id" />
        <input type="text" name="description" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="text" name="contact-method" />
        <textarea name="detail"></textarea>
      </form>

      {/* Main Component */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            marginBottom: '8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Support & Contact
            </h2>
            {tierConfig.badge && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                padding: '5px 12px',
                backgroundColor: tierConfig.badge.backgroundColor,
                color: tierConfig.badge.color,
                borderRadius: '12px',  // Rounded edges like Facility Sharing
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                {tierConfig.badge.icon && <span>{tierConfig.badge.icon}</span>}
                <span>{tierConfig.badge.text}</span>
              </span>
            )}
          </div>

          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          Get help from our support team
        </p>

        {/* Content */}
        {isExpanded && (
          <div style={{ marginTop: '24px' }}>
            {/* Tier Info Banner */}
            <div style={{
              padding: '16px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#075985',
                margin: '0 0 8px 0'
              }}>
                {tierConfig.title}
              </h3>
              <p style={{
                fontSize: '13px',
                color: '#0c4a6e',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {tierConfig.description}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                color: '#991b1b',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            {/* Support Form */}
            <form onSubmit={handleSubmit}>
              {/* Description of Issue */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Description of Issue <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief summary of your issue"
                  maxLength={100}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'text',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '4px',
                  marginBottom: 0
                }}>
                  {formData.description.length}/100 characters
                </p>
              </div>

              {/* Best Email Contact */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Best Email Contact <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'text',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Premium Only: Contact Method Selection */}
              {tierConfig.showPhone && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Preferred Contact Method <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <select
                    value={formData.contactMethod}
                    onChange={(e) => handleChange('contactMethod', e.target.value)}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>
              )}

              {/* Premium Only: Phone Number (if phone selected) */}
              {tierConfig.showPhone && formData.contactMethod === 'phone' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Best Mobile Phone Number <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="04XX XXX XXX"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                      cursor: isSubmitting ? 'not-allowed' : 'text',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Detail */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Detail <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={formData.detail}
                  onChange={(e) => handleChange('detail', e.target.value)}
                  placeholder="Please provide detailed information about your issue or question..."
                  maxLength={2000}
                  rows={6}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                    cursor: isSubmitting ? 'not-allowed' : 'text',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '4px',
                  marginBottom: 0
                }}>
                  {formData.detail.length}/2000 characters
                </p>
              </div>

              {/* Submit Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid()}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: isSubmitting || !isFormValid() ? '#9ca3af' : '#22c55e',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting || !isFormValid() ? 'not-allowed' : 'pointer',
                    color: 'white',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting && isFormValid()) {
                      e.currentTarget.style.backgroundColor = '#16a34a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting && isFormValid()) {
                      e.currentTarget.style.backgroundColor = '#22c55e';
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>📧</span>
                      <span>Submit Support Request</span>
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Direct Contact Info */}
            <div style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              <strong>Need immediate help?</strong> Email us directly at{' '}
              <a 
                href="mailto:pitchalloaustralia@gmail.com"
                style={{ color: '#2563eb', textDecoration: 'none' }}
              >
                pitchalloaustralia@gmail.com
              </a>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SupportContact;
